import useAuthStore from "@/stores/useAuthStore";
import { getVisitorId, createEventId } from "./visitorId";
import { getPreviousPath, resolveCompareSource } from "@/lib/compare/compareSource";
import { isKnownCtaType, isKnownPlacement, OUTBOUND_CTA_TYPES } from "./ctaRegistry";

/**
 * Fire-and-forget delivery of call-to-action clicks.
 *
 * Nothing here may ever surface an error, block a navigation, or stop a clipboard write.
 * A failed analytics call must be invisible on the page.
 */

// The API rejects a flush larger than this.
const MAX_EVENTS_PER_FLUSH = 25;
const FLUSH_DELAY_MS = 1500;

let buffer = [];
let timer = null;
let context = null;
let listenersBound = false;

function endpoint() {
  const base = (process.env.NEXT_PUBLIC_API_BASE_URL || "/api/v1").replace(/\/$/, "");
  return `${base}/cta/track`;
}

/**
 * The entry point is captured once and reused for the visit. The source vocabulary is
 * shared with compare analytics so "came from the home page" means the same thing on
 * both dashboards.
 */
function ensureContext() {
  if (context) return context;

  context = {
    source: resolveCompareSource(),
    referrer: (typeof document !== "undefined" && document.referrer) || undefined,
  };

  return context;
}

/**
 * Flush on page hide as well as on the debounce.
 *
 * Bound lazily on the first tracked click rather than from a hook: CTAs sit on many
 * pages, and a hook every page had to remember to mount is a hook some page eventually
 * forgets. `pagehide` is the only event that fires reliably on mobile Safari and into
 * the bfcache; `visibilitychange` covers tab switches and Android backgrounding.
 */
function bindFlushListeners() {
  if (listenersBound || typeof window === "undefined") return;
  listenersBound = true;

  const flush = () => flushCtaEvents();
  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
}

function send(payload) {
  // Read the token directly rather than going through the axios instance: `keepalive`
  // is what lets the request survive the page closing or a new tab stealing focus, and
  // unlike sendBeacon it can still carry the Authorization header, so a signed-in
  // visitor stays attributed.
  const token = useAuthStore.getState?.()?.accessToken;

  try {
    fetch(endpoint(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Never rethrow: tracking is invisible to the visitor.
  }
}

export function flushCtaEvents() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }

  if (buffer.length === 0) return;

  const visitorId = getVisitorId();
  if (!visitorId) {
    buffer = [];
    return;
  }

  const events = buffer.splice(0, MAX_EVENTS_PER_FLUSH);
  send({ visitorId, ...ensureContext(), events });

  // A burst larger than one flush keeps its ordering by going out immediately.
  if (buffer.length > 0) flushCtaEvents();
}

/**
 * Record one call-to-action click.
 *
 * `ctaType` says what was clicked; `placement` says which section of the site it sat in.
 * Both come from `ctaRegistry.js` and the API drops anything it does not recognise.
 *
 * Outbound CTAs are sent immediately. Call this **before** `window.open` or any
 * navigation: popup blockers and mobile Safari can suspend the opener's JavaScript the
 * moment a new tab takes focus, and a request queued after that line may never leave.
 */
export function trackCta({
  ctaType,
  placement,
  pagePath,
  firmSlug,
  couponId,
  couponCode,
  challengeId,
  targetUrl,
}) {
  if (typeof window === "undefined") return;

  if (!isKnownCtaType(ctaType) || !isKnownPlacement(placement)) {
    // Loud in development, silent in production: the API would drop this event anyway,
    // and a mistyped constant should be caught while the feature is being built rather
    // than noticed weeks later as a CTA missing from the dashboard.
    if (process.env.NODE_ENV !== "production") {
      console.warn("[trackCta] unknown ctaType or placement", { ctaType, placement });
    }
    return;
  }

  if (!getVisitorId()) return;

  bindFlushListeners();
  ensureContext();

  buffer.push({
    eventId: createEventId(),
    ctaType,
    placement,
    pagePath: pagePath ?? window.location.pathname,
    firmSlug: firmSlug || undefined,
    couponId: couponId || undefined,
    couponCode: couponCode || undefined,
    challengeId: challengeId || undefined,
    targetUrl: targetUrl || undefined,
    at: new Date().toISOString(),
  });

  if (OUTBOUND_CTA_TYPES.has(ctaType) || buffer.length >= MAX_EVENTS_PER_FLUSH) {
    flushCtaEvents();
    return;
  }

  if (timer) clearTimeout(timer);
  timer = setTimeout(flushCtaEvents, FLUSH_DELAY_MS);
}
